import {
    AccountLicense,
    TelemetryTitle,
} from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    Button,
    CircularProgress,
    FormControlLabel,
    Link,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { EncryptionLink } from '../../../../common/constants/documentation/DocumentationLinks.default';
import { useDocumentationLinks } from '../../../../common/hooks/useDocumentationLink';
import lockEncryption from '../../../../images/lockEncryption.svg';
import UiAlertBanner from '../../../common/UiAlertBanner';
import { UiUpgradeChip } from '../../../common/UiUpgradeChip';
import useEncryptionViewModel from './EncryptionViewModel';

const useStyles = makeStyles(theme =>
    createStyles({
        centerLoader: { margin: 'auto' },
        chipSpacer: { marginLeft: '4px' },
        cmkLabel: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        default: {
            color: theme.palette.semantic.colorForegroundDeEmp,
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            marginLeft: '4px',
        },
        description: {
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            marginBottom: '28px',
        },
        editButton: { marginLeft: '28px' },
        labelDescription: {
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            marginBottom: '4px',
        },
        labelTitle: {
            color: theme.palette.semantic.colorForegroundEmp,
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            marginBottom: '4px',
        },
        labelTitleContainer: {
            display: 'flex',
            flexDirection: 'row',
        },
        link: {
            fontWeight: 600,
            fontSize: '12px',
            lineHeight: '16px',
            marginBottom: '4px',
        },
        mainContent: { width: '100%' },
        radio: { marginBottom: '18px' },
        title: {
            color: theme.palette.semantic.colorForegroundEmp,
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: '24px',
            marginBottom: '8px',
        },
    }),
);

export enum EncryptionType {
    UIPATH = 'uipath',
    CUSTOMER = 'customer',
}

const EncryptionComponent: React.FC = () => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const EnableCustomerManagedKeyEntitlement = useFeatureFlagValue(Features.EnableCustomerManagedKeyEntitlement.name);

    const getLocalizedLink = useDocumentationLinks({ excludedLanguages: [ 'es-MX', 'ko', 'zh-TW', 'pt', 'tr', 'ru' ] });

    const {
        editConnection,
        handleChange,
        encryptionType,
        kmsConfig,
        isUserEnterprise,
        hasEntitlement,
        loading,
    } = useEncryptionViewModel();

    const options = useMemo(() => [
        {
            title: (
                <div className={clsx(classes.labelTitleContainer, classes.labelTitle)}>
                    <Typography>
                        {translate({ id: 'CLIENT_ENCRYPTION_UIPATH_MANAGED_KEY' })}
                    </Typography>
                    <Typography className={classes.default}>
                            (
                        {translate({ id: 'CLIENT_DEFAULT' })}
)
                    </Typography>
                </div>
            ),
            description: (
                <Typography className={classes.labelDescription}>
                    {translate({ id: 'CLIENT_ENCRYPTION_UIPATH_MANAGED_KEY_DESCRIPTION' })}
                </Typography>),
            value: EncryptionType.UIPATH,
            dataCy: 'uipath-managed-key',
            disabled: false,
        },
        {
            title: (
                <div className={clsx(classes.cmkLabel, classes.labelTitle)}>
                    <Typography>
                        {translate({ id: 'CLIENT_ENCRYPTION_CUSTOMER_MANAGED_KEY' })}
                    </Typography>
                    {(EnableCustomerManagedKeyEntitlement ? !hasEntitlement : !isUserEnterprise) && <UiUpgradeChip
                        className={classes.chipSpacer}
                        licenseType={AccountLicense.ENTERPRISE}
                        customLabel={EnableCustomerManagedKeyEntitlement ? translate({ id: 'CLIENT_ENTERPRISE_ADVANCE_ONLY' }) : undefined}
                        title={translate({ id: 'CLIENT_ENCRYPTION_UPGRADE_HEADER' })}
                        description={translate({ id: 'CLIENT_ENCRYPTION_UPGRADE_DESCRIPTION' })}
                        iconDescription={translate({ id: 'CLIENT_ENCRYPTION_UPGRADE_ICON_DESCRIPTION' })}
                        icon={
                            <img
                                src={lockEncryption}
                                alt='' />
                        }
                        telemetryTitle={TelemetryTitle.SecuritySettings.EncryptionChip}
                    />}
                </div>
            ),
            description: (
                <>
                    <Typography className={classes.labelDescription}>
                        {translate({ id: 'CLIENT_ENCRYPTION_CUSTOMER_MANAGED_KEY_DESCRIPTION' })}
                    </Typography>
                    <Link
                        className={classes.link}
                        href={getLocalizedLink({ articleSlug: EncryptionLink })}
                        target='_blank'
                        underline='none'
                        data-cy='encryption-learn-more-link'
                    >
                        {translate({ id: 'CLIENT_LEARN_MORE' })}
                    </Link>
                </>),
            value: EncryptionType.CUSTOMER,
            dataCy: 'customer-managed-key',
            disabled: (EnableCustomerManagedKeyEntitlement ? !hasEntitlement : !isUserEnterprise),
        },
    ], [
        EnableCustomerManagedKeyEntitlement,
        classes.chipSpacer,
        classes.cmkLabel,
        classes.default,
        classes.labelDescription,
        classes.labelTitle,
        classes.labelTitleContainer,
        classes.link,
        getLocalizedLink,
        hasEntitlement,
        isUserEnterprise,
        translate,
    ]);

    return (
        (kmsConfig && !loading) ?
            <div className={classes.mainContent}>
                {(EnableCustomerManagedKeyEntitlement ? !hasEntitlement : !isUserEnterprise) && <UiAlertBanner
                    closeable={false}
                    type='warning'>
                    {translate(
                        { id: 'CLIENT_ENCRYPTION_UPGRADE_BANNER' },
                        {
                            link: (
                                <Link
                                    target="_blank"
                                    rel="noreferrer"
                                    href={getLocalizedLink({ articleSlug: EncryptionLink })}
                                    underline='none'
                                >
                                    {translate({ id: 'CLIENT_UPGRADE_TO_ENTERPRISE_REVAMP' })}
                                </Link>
                            ),
                        },
                    )}
                </UiAlertBanner>}
                <Typography
                    id='encryptionTitle'
                    className={classes.title}
                    data-cy='encryption-title'
                >
                    {translate({ id: 'CLIENT_ENCRYPTION_TYPE' })}
                </Typography>
                <Typography
                    className={classes.description}
                    data-cy='encryption-description'
                >
                    {translate({ id: 'CLIENT_ENCRYPTION_DESCRIPTION' })}
                </Typography>
                <RadioGroup
                    aria-labelledby='encryptionTitle'
                    value={encryptionType}
                    data-cy='encryption-radio-group'
                    onChange={handleChange}
                >
                    {options.map((option, i) => (
                        <FormControlLabel
                            disabled={option.disabled}
                            className={classes.radio}
                            key={i}
                            value={option.value}
                            control={<Radio />}
                            label={<>
                                {option.title}
                                {option.description}
                            </>}
                            data-cy={`encryption-radio-${option.dataCy}`}
                        />
                    ))}
                </RadioGroup>
                {encryptionType === EncryptionType.CUSTOMER &&
            (EnableCustomerManagedKeyEntitlement ? hasEntitlement : isUserEnterprise) &&
            <Button
                className={classes.editButton}
                data-cy='encryption-edit-connection-button'
                onClick={editConnection}
                variant='outlined'>
                {translate({ id: 'CLIENT_EDIT_CONNECTION' })}
            </Button>}
            </div>
            :
            <div
                className={classes.centerLoader}
                data-cy='encryption-circular-progress'>
                <CircularProgress />
            </div>
    );
};

export default EncryptionComponent;
